import React, { useState, useEffect } from 'react';
import { X, Filter, Users, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const ReleaseSlotModal = ({ slot, selectedHour, calendarId, onClose, onConfirm, getAuthHeaders }) => {
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFilters, setExpandedFilters] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    sendNotification: false,
    notificationMethod: 'email',
    // Enhanced recipient targeting
    filterType: 'all',
    selectedCustomers: [],
    filterDays: 30,
    filterServiceId: '',
    filterDate: '',
    filterStartTime: '',
    filterEndTime: '',
    customMessage: ''
  });

  useEffect(() => {
    fetchData();
  }, [calendarId]);

  const fetchData = async () => {
    try {
      // Fetch customers registered to THIS calendar only
      const customersUrl = calendarId 
        ? `${API_BASE_URL}/calendars/${calendarId}/customers`
        : `${API_BASE_URL}/auth/users`;
      
      const [customersRes, servicesRes] = await Promise.all([
        fetch(customersUrl, {
          headers: getAuthHeaders()
        }),
        fetch(`${API_BASE_URL}/services`, {
          headers: getAuthHeaders()
        })
      ]);

      if (customersRes.ok) {
        const data = await customersRes.json();
        // Handle both response formats
        const customerList = data.data 
          ? data.data.filter(user => user.role === 'customer' || user.role === 'non_registered_customer')
          : (data.customers || []);
        setCustomers(customerList);
      }

      if (servicesRes.ok) {
        const data = await servicesRes.json();
        if (data.success) {
          setServices(data.data);
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerToggle = (customerId) => {
    setFormData(prev => ({
      ...prev,
      selectedCustomers: prev.selectedCustomers.includes(customerId)
        ? prev.selectedCustomers.filter(id => id !== customerId)
        : [...prev.selectedCustomers, customerId]
    }));
  };

  const handleSelectAll = () => {
    setFormData(prev => ({
      ...prev,
      selectedCustomers: customers.map(c => c.id)
    }));
  };

  const handleDeselectAll = () => {
    setFormData(prev => ({
      ...prev,
      selectedCustomers: []
    }));
  };

  const buildRecipientFilter = () => {
    const filter = { filter_type: formData.filterType };
    
    if (formData.filterType === 'manual') {
      filter.customer_ids = formData.selectedCustomers;
    } else if (formData.filterType === 'last_n_days') {
      filter.days = parseInt(formData.filterDays);
    } else if (formData.filterType === 'service') {
      filter.service_id = formData.filterServiceId;
    } else if (formData.filterType === 'time_slot') {
      filter.date = formData.filterDate;
      filter.start_time = formData.filterStartTime;
      filter.end_time = formData.filterEndTime;
    }
    
    return filter;
  };

  const previewRecipients = async () => {
    try {
      setPreviewLoading(true);
      const response = await fetch(`${API_BASE_URL}/marketing/preview-recipients`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(buildRecipientFilter())
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setPreviewCount(data.data.count);
        }
      }
    } catch (err) {
      console.error('Error previewing recipients:', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = () => {
    // Build notification data in the format expected by CalendarView
    const recipientFilter = buildRecipientFilter();
    
    const notificationData = formData.sendNotification ? {
      send: true,
      method: formData.notificationMethod,
      // Convert filter to the format expected by the API
      recipientType: formData.filterType === 'manual' ? 'selected' : 'all',
      selectedCustomers: formData.filterType === 'manual' ? formData.selectedCustomers : [],
      message: formData.customMessage
    } : {
      send: false
    };

    onConfirm(notificationData);
  };

  const formatSlotTime = () => {
    if (!slot) return '';
    // Use selectedHour if available (single hour slot), otherwise fall back to slot times
    if (selectedHour && selectedHour.time) {
      const startHour = parseInt(selectedHour.time.split(':')[0]);
      const endHour = startHour + 1;
      const endTime = `${endHour.toString().padStart(2, '0')}:00`;
      return `${selectedHour.date} from ${selectedHour.time} to ${endTime}`;
    }
    return `${slot.date} from ${slot.start_time} to ${slot.end_time}`;
  };

  const filterOptions = [
    { value: 'all', label: 'All Customers' },
    { value: 'manual', label: 'Manual Selection' },
    { value: 'last_n_days', label: 'Booked Last N Days' },
    { value: 'service', label: 'By Service' },
    { value: 'time_slot', label: 'By Time Slot' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Release Slot</h2>
            <p className="text-sm text-gray-600 mt-1">{formatSlotTime()}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="close-modal-btn"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Send Notification Toggle */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="sendNotification"
              checked={formData.sendNotification}
              onChange={(e) => setFormData({ ...formData, sendNotification: e.target.checked })}
              className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
              data-testid="send-notification-checkbox"
            />
            <label htmlFor="sendNotification" className="ml-3 text-sm font-medium text-gray-700">
              Send notification to customers
            </label>
          </div>

          {formData.sendNotification && (
            <>
              {/* Notification Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Method
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="email"
                      checked={formData.notificationMethod === 'email'}
                      onChange={(e) => setFormData({ ...formData, notificationMethod: e.target.value })}
                      className="w-4 h-4 text-yellow-500 border-gray-300 focus:ring-yellow-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Email</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="sms"
                      checked={formData.notificationMethod === 'sms'}
                      onChange={(e) => setFormData({ ...formData, notificationMethod: e.target.value })}
                      className="w-4 h-4 text-yellow-500 border-gray-300 focus:ring-yellow-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">SMS</span>
                  </label>
                </div>
              </div>

              {/* Enhanced Recipient Targeting */}
              <div className="bg-blue-50 rounded-lg p-4">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedFilters(!expandedFilters)}
                >
                  <div className="flex items-center">
                    <Filter className="w-5 h-5 text-blue-500 mr-2" />
                    <span className="font-medium text-gray-700">Recipient Targeting</span>
                    <span className="ml-2 text-sm text-gray-500">
                      ({formData.filterType === 'all' ? 'All customers' : 
                        formData.filterType === 'manual' ? `${formData.selectedCustomers.length} selected` :
                        formData.filterType})
                    </span>
                  </div>
                  {expandedFilters ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>

                {expandedFilters && (
                  <div className="mt-4 space-y-4">
                    {/* Filter Type Selection */}
                    <div>
                      <label className="block text-sm text-gray-600 mb-2">Filter Type</label>
                      <div className="grid grid-cols-2 gap-2">
                        {filterOptions.map(opt => (
                          <label key={opt.value} className="flex items-center p-2 bg-white rounded border cursor-pointer hover:bg-gray-50">
                            <input
                              type="radio"
                              value={opt.value}
                              checked={formData.filterType === opt.value}
                              onChange={(e) => setFormData({ ...formData, filterType: e.target.value })}
                              className="w-4 h-4 text-yellow-500"
                            />
                            <span className="ml-2 text-sm">{opt.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Filter-specific inputs */}
                    {formData.filterType === 'last_n_days' && (
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Number of Days</label>
                        <input
                          type="number"
                          value={formData.filterDays}
                          onChange={(e) => setFormData({ ...formData, filterDays: parseInt(e.target.value) })}
                          className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm"
                          min={1}
                          max={365}
                        />
                        <p className="text-xs text-gray-500 mt-1">Customers who booked in the last {formData.filterDays} days</p>
                      </div>
                    )}

                    {formData.filterType === 'service' && (
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Service</label>
                        <select
                          value={formData.filterServiceId}
                          onChange={(e) => setFormData({ ...formData, filterServiceId: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        >
                          <option value="">-- Select Service --</option>
                          {services.map(s => (
                            <option key={s.id} value={s.id}>{s.service_name}</option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Customers who have booked this service</p>
                      </div>
                    )}

                    {formData.filterType === 'time_slot' && (
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">Date</label>
                          <input
                            type="date"
                            value={formData.filterDate}
                            onChange={(e) => setFormData({ ...formData, filterDate: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">Start Time</label>
                          <input
                            type="time"
                            value={formData.filterStartTime}
                            onChange={(e) => setFormData({ ...formData, filterStartTime: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">End Time</label>
                          <input
                            type="time"
                            value={formData.filterEndTime}
                            onChange={(e) => setFormData({ ...formData, filterEndTime: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                      </div>
                    )}

                    {/* Manual Customer Selection */}
                    {formData.filterType === 'manual' && (
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <label className="text-sm text-gray-600">
                            Select Customers ({formData.selectedCustomers.length} selected)
                          </label>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={handleSelectAll}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              Select All
                            </button>
                            <span className="text-xs text-gray-400">|</span>
                            <button
                              type="button"
                              onClick={handleDeselectAll}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              Clear
                            </button>
                          </div>
                        </div>
                        <div className="border border-gray-200 rounded-md max-h-48 overflow-y-auto bg-white">
                          {loading ? (
                            <div className="p-4 text-center text-gray-500">Loading customers...</div>
                          ) : customers.length === 0 ? (
                            <div className="p-4 text-center text-gray-500">No customers found</div>
                          ) : (
                            <div className="divide-y">
                              {customers.map(customer => (
                                <label
                                  key={customer.id}
                                  className="flex items-center p-3 hover:bg-gray-50 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={formData.selectedCustomers.includes(customer.id)}
                                    onChange={() => handleCustomerToggle(customer.id)}
                                    className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                                  />
                                  <div className="ml-3">
                                    <div className="text-sm font-medium text-gray-900">{customer.full_name}</div>
                                    <div className="text-xs text-gray-500">{customer.email}</div>
                                  </div>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Preview Recipients Button */}
                    <div className="flex items-center justify-between pt-2 border-t">
                      <button
                        type="button"
                        onClick={previewRecipients}
                        disabled={previewLoading}
                        className="text-sm text-blue-600 hover:underline flex items-center"
                      >
                        {previewLoading ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Users className="w-4 h-4 mr-1" />
                        )}
                        Preview Recipients
                      </button>
                      {previewCount !== null && (
                        <span className="text-sm text-gray-600">
                          {previewCount} recipients found
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Note Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Note <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <textarea
                  value={formData.customMessage}
                  onChange={(e) => setFormData({ ...formData, customMessage: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  rows="3"
                  placeholder="Add a custom note (e.g., 'Book early - limited spots!')"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use <span className="font-mono bg-gray-100 px-1 rounded">&note</span> in your Release Slot template to include this note
                </p>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-md transition-colors"
            data-testid="cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={formData.sendNotification && formData.filterType === 'manual' && formData.selectedCustomers.length === 0}
            className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 disabled:bg-yellow-300 disabled:cursor-not-allowed text-white font-semibold rounded-md transition-colors"
            data-testid="release-slot-btn"
          >
            Release Slot
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReleaseSlotModal;
